import React from "react";
import CcToolFormOption from "./CcToolFormOption";
import { cx } from "../utils";

/**
 * CcToolFormSection — tool template question block (radio or checkbox group).
 */
const READ_ROW = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  padding: "8px 12px",
  borderRadius: 8,
  background: "var(--cc-bg-bone)",
  border: "1px solid var(--cc-line)",
};
const READ_LABEL = { fontSize: "0.875rem", color: "var(--cc-text-ink)" };
const READ_SCORE = {
  fontSize: "0.75rem",
  fontWeight: 700,
  color: "var(--cc-terra)",
  flexShrink: 0,
};
const READ_EMPTY = {
  fontSize: "0.8125rem",
  color: "var(--cc-text-quiet)",
  margin: 0,
};

const CcToolFormSection = ({
  title,
  fields = [],
  value,
  allowMultiple = false,
  disabled = false,
  readOnly = false,
  onChange,
  fieldErrors = {},
  sectionIndex = 0,
  className,
}) => {
  if (!fields.length) return null;

  const groupName = `cc-tool-section-${sectionIndex}`;

  const handleSelect = (fieldId, checked) => {
    if (!onChange) return;

    if (allowMultiple) {
      const current = Array.isArray(value) ? value : [];
      const next = checked
        ? [...current, fieldId]
        : current.filter((id) => id !== fieldId);
      onChange(next);
      return;
    }

    onChange(fieldId);
  };

  const isSelected = (fieldId) => {
    if (allowMultiple) {
      return Array.isArray(value) && value.includes(fieldId);
    }
    return value === fieldId;
  };

  // Read-only view: show only the chosen answer(s) as a clean statement, not a
  // disabled radio/checkbox list.
  if (readOnly) {
    const selectedFields = fields.filter((field) => isSelected(field.id));
    return (
      <section className={cx("cc-tool-form-section", className)}>
        <h3 className="cc-tool-form-section__title">{title}</h3>
        {selectedFields.length ? (
          <div className="cc-tool-form-section__fields">
            {selectedFields.map((field) => (
              <div key={field.id} style={READ_ROW}>
                <span style={READ_LABEL}>{field.label}</span>
                {typeof field.score === "number" ? (
                  <span style={READ_SCORE}>{field.score}</span>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          <div className="cc-tool-form-section__fields">
            <p style={READ_EMPTY}>Not answered</p>
          </div>
        )}
      </section>
    );
  }

  return (
    <section className={cx("cc-tool-form-section", className)}>
      <h3 className="cc-tool-form-section__title">{title}</h3>
      <div
        className="cc-tool-form-section__fields"
        role={allowMultiple ? "group" : "radiogroup"}
        aria-label={title}
      >
        {fields.map((field) => {
          const errorKey = `${sectionIndex}_${field.id}`;
          const error = fieldErrors[errorKey];
          const selected = isSelected(field.id);

          return (
            <div key={field.id} className="cc-tool-form-section__field-wrap">
              <CcToolFormOption
                label={field.label}
                score={field.score}
                selected={selected}
                multiple={allowMultiple}
                disabled={disabled}
                name={groupName}
                value={field.id}
                onChange={(e) =>
                  handleSelect(
                    field.id,
                    allowMultiple ? e.target.checked : true,
                  )
                }
              />
              {error ? (
                <p className="cc-tool-form-section__error" role="alert">
                  {error}
                </p>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default CcToolFormSection;
